import type { RemotePhoto } from 'components/PhotoBank/PhotoBank.types'
import type { Feature } from 'services/ExperimentManager/Experiments.consts'
import type { CartUpsellId, CartUpsellOffer } from './cartUpsells.schemas'
import type {
  CartUpsellAnalyticsProps,
  CartUpsellThumbnail,
} from './cartUpsells.types'
import { BATH_TIME } from './petAiWall/bathTime'
import { createPetAiWallUpsell } from './petAiWall/createPetAiWallUpsell'

export type CartUpsellFlowProps<Id extends CartUpsellId> = {
  offer: CartUpsellOffer<Id>
  isOpen: boolean
  onClose: () => void
  onAddedToBag: () => void
  // The sheet finished animating out, so the Flow can unmount.
  onExited: () => void
  analyticsProps: CartUpsellAnalyticsProps
}

export type CartUpsellDefinition<Id extends CartUpsellId> = {
  experimentFlag: Feature
  getCardTitleKey: (offer: CartUpsellOffer<Id>) => string
  cardCtaKey: string
  getThumbnail: (
    offer: CartUpsellOffer<Id>,
    sourcePhotos: RemotePhoto[]
  ) => CartUpsellThumbnail
  // What the Flow shows, so a rendered card can load it before it is tapped.
  getFlowImageUrls: (offer: CartUpsellOffer<Id>) => string[]
  // What the offer's own events add beyond its id, e.g. the pet a pet AI wall was generated from.
  getAnalyticsProps: (offer: CartUpsellOffer<Id>) => Record<string, unknown>
  Producer: React.ComponentType
  Flow: React.ComponentType<CartUpsellFlowProps<Id>>
}

export const CART_UPSELLS: {
  [Id in CartUpsellId]: CartUpsellDefinition<Id>
} = {
  'bath-time': createPetAiWallUpsell(BATH_TIME),
}

export function getCartUpsellCardTitleKey<Id extends CartUpsellId>(
  id: Id,
  offer: CartUpsellOffer<Id>
): string {
  return CART_UPSELLS[id].getCardTitleKey(offer)
}

export function getCartUpsellThumbnail<Id extends CartUpsellId>(
  id: Id,
  offer: CartUpsellOffer<Id>,
  sourcePhotos: RemotePhoto[]
): CartUpsellThumbnail {
  return CART_UPSELLS[id].getThumbnail(offer, sourcePhotos)
}

export function getCartUpsellFlowImageUrls<Id extends CartUpsellId>(
  id: Id,
  offer: CartUpsellOffer<Id>
): string[] {
  return CART_UPSELLS[id].getFlowImageUrls(offer)
}

export function getCartUpsellAnalyticsProps<Id extends CartUpsellId>(
  id: Id,
  offer: CartUpsellOffer<Id>
): CartUpsellAnalyticsProps {
  return { 'Upsell ID': id, ...CART_UPSELLS[id].getAnalyticsProps(offer) }
}
